/**
 * Configuration for QLE document upload page copy.
 * Maps enrollment_subtype to the appropriate instructional text and document examples.
 */

export type QleDocumentUploadCopyConfig = {
  introText: string
  documentExamples: string[]
  note?: string
}

/*
* Missing subtypes: thatch_incident, loss_of_subsidy_eligibility, cobra_expiration
* We made a product choice to not allow these subtypes to be selected by our users. They will not be relevant in the document upload page.
*/
export const QLE_DOCUMENT_UPLOAD_COPY: Record<string, QleDocumentUploadCopyConfig> = {
  marriage: {
    introText: 'For your marriage, that\'d be either a:',
    documentExamples: [
      'Marriage certificate, or a',
      'Marriage license',
    ],
  },
  divorce_or_legal_separation: {
    introText: 'For divorces, we specifically need to see that you or a family member lost insurance coverage due to the event — unfortunately, the divorce alone doesn\'t qualify. So we\'d be looking for something like a:',
    documentExamples: [
      'A letter from your current insurance company stating that your coverage is ending,',
      'A divorce decree',
    ],
  },
  relocation: {
    introText: 'Insurance companies only offer their plans in certain areas, so they need to see that you\'re living somewhere that they service. We need you to submit at least two (2) documents that prove you live at your new address, like a:',
    documentExamples: [
      'Utility bill (or other mail that indicates that theresidence is yours),',
      'Signed lease or home title, or',
      'Photo ID that shows the new address',
    ],
  },
  birth_or_adoption: {
    introText: 'For adding a child, we\'ll need to see documentation like a:',
    documentExamples: [
      'Birth certificate,',
      'Adoption papers, or',
      'Court-ordered placement documents',
    ],
  },
  loss_of_coverage: {
    introText: 'You\'re allowed to change your coverage if you or someone in your family lost insurance coverage. There could be a few different ways that you show this, but the most common are things like:',
    documentExamples: [
      'Insurance company letter that mentions the person\'s name and when their coverage is ending,',
      'Termination or severance letter from their company where they received job-based coverage,',
      'Government document showing you\'re newly-eligible for insurance subsidies or tax credits,',
      'Government document showing you\'re no longer eligible for Medicaid or CHIP, or',
      'Birth certificate for a dependent that\'s aging out of their parent\'s coverage,',
    ],
    note: 'Note: loss of insurance coverage due to non-payment is not allowed as a qualifying life event.',
  },
  death_of_dependent: {
    introText: 'For deaths, we specifically need to see that you or a family member lost insurance due to the event — unfortunately, the death alone doesn\'t qualify. (We\'re very sorry for your loss, either way.)\n\nThis means we need to ask you for something like:',
    documentExamples: [
      'Insurance company letter that shows the deceased as the primary policyholder, or a',
      'Death certificate',
    ],
  },
  left_incarceration: {
    introText: 'If you recently left incarceration, you\'ll need to provide something like your discharge papers.',
    documentExamples: [],
  },
  immigrated: {
    introText: 'For immigration status changes (congratulations!), this is usually something that proves that change, like a:',
    documentExamples: [
      'Visa filing, or',
      'Court document',
    ],
  },
  court_ordered: {
    introText: 'If you gained a dependent, or became someone else\'s dependent, due to a court order, that proof will be the court order itself.',
    documentExamples: [],
  },
  // TODO: Remove this once we remove document upload from the medicare QLE flow
  medicare: {
    introText: '[Medicare does not require document upload - this page can be skipped]',
    documentExamples: [],
  },
  other: {
    introText: 'Since you underwent a unique scenario, what constitutes sufficient proof will vary based on your circumstances, but could be things like:',
    documentExamples: [
      'FEMA declaration of emergency in your area of residence,',
      'Hospital records of an unexpected hospitalization or incapacitation during regular enrollment periods,',
      'Joining or discharge papers for programs like AmeriCorps, VISTA, or NCCC, or a',
      'Government document for gaining membership in a federally-recognized tribe or status as an Alaska native',
    ],
    note: 'If you\'re not sure what would apply in your case, you can reach out to support@thatch.com for help.',
  },
}
